import { useState } from 'react';
import { Button, IconButton, Tooltip, Chip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { whatsappTemplatesApi, type WhatsAppTemplate, type CreateTemplateInput } from '../../api/whatsapp.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const empty: CreateTemplateInput = { code: '', name: '', body: '', status: 'active' };

export function WhatsAppTemplatesPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WhatsAppTemplate | null>(null);
  const [form, setForm] = useState<CreateTemplateInput>(empty);
  const [deleting, setDeleting] = useState<WhatsAppTemplate | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['whatsapp-templates', page, limit], queryFn: () => whatsappTemplatesApi.list({ page, limit }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['whatsapp-templates'] });

  const saveMut = useMutation({
    mutationFn: () => (editing ? whatsappTemplatesApi.update(editing.id, { name: form.name, body: form.body, category: form.category, status: form.status }) : whatsappTemplatesApi.create(form)),
    onSuccess: () => { setOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setErr(extractApiError(e)),
  });
  const deleteMut = useMutation({ mutationFn: (id: number) => whatsappTemplatesApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });

  const openCreate = () => { setEditing(null); setForm(empty); setErr(null); setOpen(true); };
  const openEdit = (t: WhatsAppTemplate) => { setEditing(t); setForm({ code: t.code, name: t.name, body: t.body, category: t.category ?? undefined, status: t.status }); setErr(null); setOpen(true); };

  const columns: Column<WhatsAppTemplate>[] = [
    { key: 'code', label: 'Code', width: 200 },
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Category', render: (r) => r.category ?? '—' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader title="WhatsApp Templates" subtitle="Message templates with {{placeholders}} for the communication hub."
        action={hasPermission('whatsapp_template.create') && <Button startIcon={<Add />} variant="contained" onClick={openCreate}>New Template</Button>} />
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('whatsapp_template.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('whatsapp_template.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? `Edit — ${editing.code}` : 'New WhatsApp Template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {err && <Alert severity="error">{err}</Alert>}
            <TextField label="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} disabled={Boolean(editing)} fullWidth />
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Category" value={form.category ?? ''} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} fullWidth />
            <TextField label="Body" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} fullWidth multiline minRows={3} helperText="Use {{placeholders}} e.g. Hi {{dealerName}}" />
            <TextField select label="Status" value={form.status ?? 'active'} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} fullWidth>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.code || !form.name || !form.body} onClick={() => saveMut.mutate()}>{editing ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleting)} title="Delete template" message={`Delete ${deleting?.code}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
